import { IconSearch } from '@developerz/ui';
import { useNavigate } from '@solidjs/router';
import { createEffect, createMemo, createSignal, For, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import {
  buildSection,
  type Command,
  optionDomId,
  pageCommands,
  repoCommands,
  sessionCommands,
} from '../lib/command-palette';
import { useDialogTrap } from '../lib/dialog-trap';
import { useScrollLock } from '../lib/overlay';
import { useAccountSessions } from '../lib/overview';
import { useRepoList } from '../lib/repos';
import { SESSION_TOKEN } from '../lib/session';
import { CommandPaletteGroup, groupHeading } from './CommandPaletteGroup';

// The palette's guts: search box, the three sections, and the whole keyboard
// contract. Mounted only while the palette is on screen (<CommandPalette/> gates
// it behind `useClosing`), which is what keeps its two reads OFF every other
// page — an always-mounted palette would add a 30s `/v1/sessions` poll and a
// full `/v1/repos` walk to every screen in the app.
//
// Escape / Tab-trap / focus-restore stay `useDialogTrap` keyed off the RAW open
// prop, so focus returns to whatever opened the palette the instant it closes,
// not when the exit animation finishes.

const RESULTS_ID = 'palette-results';

export function CommandPaletteDialog(props: {
  readonly open: boolean;
  readonly adminHost: boolean;
  readonly resolve: (href: string) => string;
  readonly onClose: () => void;
  /** Test seam, like every route's own `token` prop. Defaults to `SESSION_TOKEN`. */
  readonly token?: string | null;
}): JSX.Element {
  let dialog: HTMLDivElement | undefined;
  const navigate = useNavigate();
  const [query, setQuery] = createSignal('');
  const [cursor, setCursor] = createSignal(0);
  useDialogTrap(
    () => props.open,
    () => dialog,
    () => props.onClose(),
  );
  useScrollLock(() => props.open);

  // Both server-backed groups IDLE on the admin radar: it is cross-account by
  // definition (#754), so there is no org whose repos and sessions these would
  // be. `enabled: token !== null` is the gate both hooks already carry — no
  // second one, and no call that could only ever 403.
  const token = props.adminHost ? null : props.token === undefined ? SESSION_TOKEN : props.token;
  const repos = useRepoList(token);
  const sessions = useAccountSessions(token);

  const sections = createMemo(() => [
    buildSection('pages', pageCommands(props.adminHost, props.resolve), query(), false),
    buildSection('repos', repoCommands(repos.rows(), props.resolve), query(), repos.view.isError),
    buildSection(
      'sessions',
      sessionCommands(sessions.data ?? [], props.resolve),
      query(),
      sessions.isError,
    ),
  ]);
  /**
   * The refused groups, as KEYS. `sections()` rebuilds every object on each
   * keystroke; keyed on those, the live region below re-rendered and re-announced
   * the same failure per character. Strings compare by value, so `<For>` keeps
   * the nodes, and the memo only fires when the set of failures changes.
   */
  const refused = createMemo(
    () => sections().flatMap((section) => (section.failed ? [section.key] : [])),
    [],
    { equals: (a, b) => a.length === b.length && a.every((key, i) => key === b[i]) },
  );
  /** Every visible row, in render order — the list the arrow keys walk. */
  const flat = createMemo(() => sections().flatMap((section) => section.rows));

  // The cursor is CLAMPED rather than corrected: a background refetch can shrink
  // the list under it, and a stale index must not select nothing while rows are
  // on screen. Typing resets it, since the row under the old index is unrelated
  // to the new query.
  const active = (): number => Math.min(cursor(), Math.max(flat().length - 1, 0));
  const selected = (): Command | undefined => flat()[active()];
  createEffect(on(query, () => setCursor(0)));

  // Keep the cursor row in view when the arrows walk past the fold.
  createEffect(() => {
    const id = selected()?.id;
    if (id === undefined) return;
    document.getElementById(optionDomId(id))?.scrollIntoView({ block: 'nearest' });
  });

  const go = (command: Command | undefined): void => {
    if (command === undefined) return;
    // Close FIRST so the trap's focus-restore lands on the trigger that is still
    // mounted; restoring after a route swap steals focus from the new page.
    props.onClose();
    navigate(command.href);
  };

  const move = (delta: number): void => {
    const total = flat().length;
    if (total === 0) return;
    setCursor((active() + delta + total) % total);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      go(selected());
    }
  };

  const activeDescendant = (): string | undefined => {
    const command = selected();
    return command === undefined ? undefined : optionDomId(command.id);
  };

  return (
    <div
      class="palette__panel"
      role="dialog"
      aria-modal="true"
      aria-label={t('palette.title')}
      tabindex="-1"
      ref={dialog}
    >
      <div class="palette__search">
        <IconSearch class="palette__search-icon" size="1rem" />
        <input
          class="palette__input"
          type="text"
          role="combobox"
          aria-expanded={true}
          aria-controls={RESULTS_ID}
          aria-autocomplete="list"
          aria-activedescendant={activeDescendant()}
          aria-label={t('palette.input_label')}
          placeholder={t('palette.placeholder')}
          value={query()}
          onInput={(event) => setQuery(event.currentTarget.value)}
          onKeyDown={onKeyDown}
        />
      </div>

      <div class="palette__results" id={RESULTS_ID} role="listbox" aria-label={t('palette.title')}>
        <For each={sections()}>
          {(section) => (
            <CommandPaletteGroup section={section} activeId={selected()?.id} onPick={go} />
          )}
        </For>
      </div>

      {/* A refused source, announced. The visible note sits in its group, but
          a live region inside the listbox is a child it may not own (axe
          `aria-required-children`), so the announcement lives out here. */}
      <p class="sr-only" role="status">
        <For each={refused()}>
          {(key) => <span>{t('palette.group_failed_announce', { group: groupHeading(key) })}</span>}
        </For>
      </p>

      <Show when={flat().length === 0}>
        <p class="palette__empty">{t('palette.empty')}</p>
      </Show>

      {/* Only the keys that work HERE. `?` is not one of them — it is a
          character this input is entitled to receive. */}
      <p class="palette__foot">{t('palette.foot')}</p>
    </div>
  );
}
